/**
 * Разбор конверта ответа: всё, что можно решить без разметки.
 *
 * Здесь живут три правила, каждое из которых легко нарушить в JSX и невозможно
 * заметить глазом:
 *
 * * **величина без значения — «нет данных», а не ноль.** Конверт различает
 *   `missing` и число; клиент обязан различать их тоже, иначе пустая клетка
 *   станет нулём ровно там, где отсутствие данных и есть ответ;
 * * **текст приходит с сервера.** Фраза несёт `text`, собранный словарём
 *   шаблонов; клиент его ПЕЧАТАЕТ, а не собирает. Второго словаря в продукте
 *   нет, и функции «подставить слот» здесь тоже нет — по той же причине;
 * * **ссылка ведёт только на известный экран.** Маршрут, которого у клиента
 *   нет, ссылкой не становится: неработающая ссылка хуже, чем названный
 *   словами переход, которого пока нет.
 */

import type {
  AiBlock,
  AiPhrase,
  AiResolvedAnswer,
  AiResolvedValue,
  AiValueRef,
} from "../../api/client";
import { formatArea, formatCount, formatDate, missingLabel } from "../../lib/format";
import { LEVELS, levelPath, type LevelId } from "../../lib/levels";
import { ROUTES } from "../../lib/routes";
import { defineMessages } from "../../i18n/define";
import { tr, numberLocale } from "../../i18n/runtime";
import { srv } from "../../i18n/serverText";
import { UNIT_LABELS } from "../../i18n/hooks";

const m = defineMessages({
  day: {
    id: "answer.unit.day",
    defaultMessage: "дн.",
    description: "Единица «дни» после числа в ответе ИИ, сокращение: «12 дн.»; коротко",
  },
});

/** Единица величины контракта. Строкой её здесь нет: набор закрыт сервером. */
export type Unit = NonNullable<AiValueRef["unit"]>;

export interface ShownValue {
  text: string;
  /** Значения нет в корпусе. Не ноль и не пустая строка. */
  missing: boolean;
}

const NBSP = " ";

/**
 * Единица величины: сначала ссылка блока, потом сама величина.
 *
 * Обе стороны контракта носят единицу, и расходиться они не должны — но если
 * разошлись, верит клиент СПЕЦИФИКАЦИИ: единицу подписи выбирает тот, кто
 * строит блок, а `count` у значения — это умолчание схемы, то есть «не
 * сказано», а не «штуки».
 */
function unitOf(value: AiResolvedValue, ref?: AiValueRef): Unit {
  if (ref?.unit && ref.unit !== "count") return ref.unit;
  if (value.kind === "number" && value.unit) return value.unit;
  return "count";
}

function withUnit(text: string, unit: Unit): string {
  switch (unit) {
    case "percent":
      return `${text}${NBSP}%`;
    case "hectare":
      return `${text}${NBSP}${tr(UNIT_LABELS.ha)}`;
    case "day":
      return `${text}${NBSP}${tr(m.day)}`;
    default:
      return text;
  }
}

/** Число как его читает человек: разряды по-русски, единица рядом. */
function showNumber(value: number, unit: Unit): ShownValue {
  if (unit === "microhectare") {
    const area = formatArea(value);
    return { text: `${area.text}${NBSP}${tr(UNIT_LABELS.ha)}`, missing: area.missing };
  }
  if (unit === "percent") {
    return {
      text: withUnit(
        value.toLocaleString(numberLocale(), { maximumFractionDigits: 1 }),
        unit,
      ),
      missing: false,
    };
  }
  if (Number.isInteger(value)) {
    return { text: withUnit(formatCount(value).text, unit), missing: false };
  }
  return {
    text: withUnit(
      value.toLocaleString(numberLocale(), { maximumFractionDigits: 2 }),
      unit,
    ),
    missing: false,
  };
}

/**
 * Значение по ссылке блока. Текстовое значение — имя из данных или системное название
 * («Хлопок-2026: Сбор №1»): системные переводятся словарём сервера (`srv`), имя хозяйства
 * в словаре не найдётся и останется как в данных.
 *
 * Ссылки, которой нет в `resolved`, быть не должно — но если она есть, это
 * «нет данных», а не ноль и не пустота: придумывать за сервер клиент не имеет
 * права, а молча ничего не печатать — значит потерять клетку таблицы.
 */
export function showValue(
  resolved: AiResolvedAnswer | undefined,
  ref: AiValueRef | undefined,
): ShownValue {
  if (!ref) return { text: missingLabel(), missing: true };
  const value = resolved?.values?.[ref.id];
  if (!value || value.kind === "missing") {
    return { text: missingLabel(), missing: true };
  }
  if (value.kind === "number") return showNumber(value.value, unitOf(value, ref));
  if (value.kind === "date") {
    return { text: formatDate(value.value), missing: false };
  }
  return { text: srv(value.value), missing: false };
}

/**
 * Значение по идентификатору слота — без ссылки блока.
 *
 * Нужно там, где адрес величины несёт СЛОТ, а не `ValueRef`: имя сущности в
 * сравнении, дата точки ряда. Подделывать ради них ссылку с выдуманной
 * единицей нельзя: единица — утверждение о величине, и «пусть будет штуки»
 * здесь означало бы подпись, которой никто не давал.
 */
export function showById(
  resolved: AiResolvedAnswer | undefined,
  id: string,
): ShownValue {
  const value = resolved?.values?.[id];
  if (!value || value.kind === "missing") {
    return { text: missingLabel(), missing: true };
  }
  if (value.kind === "number") return showNumber(value.value, unitOf(value));
  if (value.kind === "date") {
    return { text: formatDate(value.value), missing: false };
  }
  return { text: srv(value.value), missing: false };
}

/** Число величины — для тех примитивов, которым нужно число, а не строка. */
export function numberOf(
  resolved: AiResolvedAnswer | undefined,
  ref: AiValueRef | undefined,
): number | null {
  if (!ref) return null;
  const value = resolved?.values?.[ref.id];
  if (!value || value.kind !== "number") return null;
  return value.value;
}

/**
 * Слова фразы. `null` означает ошибку сборки ответа на сервере, и клиент
 * говорит об этом прямо: печатать идентификатор шаблона человеку незачем, а
 * молчать — значит показать пустое место там, где был текст.
 *
 * Сервер пишет фразу по-русски (ответ от языка не зависит), поэтому на другом
 * языке её текст ищется в словаре системных текстов (`srv`, группа `answer`:
 * каждый шаблон `ai_gateway/templates.yaml` есть в словаре, это держит тест).
 * Второго словаря шаблонов клиент не заводит: строка сервера — ключ перевода.
 */
export function phraseText(phrase: AiPhrase | null | undefined): string | null {
  if (!phrase || phrase.text === null || phrase.text === undefined) return null;
  return srv(phrase.text);
}

/* ------------------------------------------------------------------ */
/* Маршруты действий                                                   */
/* ------------------------------------------------------------------ */

const LEVEL_IDS: readonly LevelId[] = LEVELS.map((level) => level.id);

/** Адреса без ключа: экраны продукта и списки уровней. */
const EXACT: ReadonlySet<string> = new Set<string>([
  ...Object.values(ROUTES),
  ...LEVEL_IDS.map((id) => levelPath(id, null)),
]);

/**
 * Знает ли клиент этот адрес.
 *
 * Сервер проверяет то же самое своим правилом 11, и всё же проверка здесь не
 * лишняя: список экранов живёт у клиента, а сервер знает его выгрузкой. Пока
 * выгрузка не обновлена, честнее назвать переход словами, чем дать ссылку в
 * «страницу не найдена».
 */
export function knownRoute(to: string): boolean {
  if (!to.startsWith("/")) return false;
  const path = (to.split("?")[0] ?? to).replace(/\/+$/, "") || "/";
  if (EXACT.has(path)) return true;
  const cut = path.lastIndexOf("/");
  const key = path.slice(cut + 1);
  if (!/^\d+$/.test(key)) return false;
  return LEVEL_IDS.some((id) => levelPath(id, key) === path);
}

/* ------------------------------------------------------------------ */
/* Бюджет плотности                                                    */
/* ------------------------------------------------------------------ */

/**
 * Первый взгляд ответа и его раскрытие.
 *
 * Сборщик раскладки на сервере уже перенёс переполнение блоков в
 * `spec.disclosure`. Здесь режется то, что сервер перенести не может: длина
 * СПИСКА внутри блока и число строк таблицы — они принадлежат одному блоку, и
 * делить блок надвое сервер не имеет права.
 */
export function splitByBudget<T>(
  items: readonly T[],
  limit: number,
): { shown: T[]; hidden: T[] } {
  return { shown: items.slice(0, limit), hidden: items.slice(limit) };
}

/** Тип блока — для перечислителя рендерера и его теста. */
export function blockType(block: AiBlock): AiBlock["type"] {
  return block.type;
}
